import { useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLocationEventsQuery } from '../components/api/queries/useLocationsQuery';
import ViewLargerButton from '../components/common/ViewLargerButton';
import ReportLocationEventBottomDrawer from '../components/drawer/ReportLocationEventBottomDrawer';
import CheckIcon from '../components/icons/CheckIcon';
import FullScreenImageSlider from '../components/images/FullScreenImageSlider';
import LocationBottomBar from '../components/offlineLocation/LocationBottomBar';
import ReportLocationEventBox from '../components/offlineLocation/ReportLocationEventBox';
import GridSkeletons from '../components/skeletons/GridSkeletons';
import BaseToastMessageBox from '../components/toast/BaseToastMessageBox';
import { ReactComponent as InformationIcon } from '../icons/card.fill.svg';
import { ReactComponent as PresentIcon } from '../icons/present.fill.svg';
import EventGoodsByCategory from '../presentation/components/event-detail/EventGoodsByCategory';
import EventGoodsGiftTypes from '../presentation/components/event-detail/EventGoodsGiftTypes';
import EventHeader from '../presentation/components/event-detail/EventHeader';
import EventPoster from '../presentation/components/event-detail/EventPoster';
import EventPosts from '../presentation/components/event-detail/EventPosts';
import PostItEventPosts from '../presentation/components/event-detail/PostItEventPosts';
import RelatedEvents from '../presentation/components/event-detail/RelatedEvents';
import { useLocationEventDetail } from '../presentation/hooks/useLocationEventDetail';
import { ellipsis } from '@pocapan/shared/utils/TextUtil';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { useReportEventAddDetailsStore } from '../presentation/report/store/useReportEventAddDetailsStore';
import BoxTabs from './tabs/BoxTabs';

const move_icon = '/assets/icons/offlineLocation/move_icon.png';

const OfflineEventDetailPage = () => {
  const { location_id: locationId, event_id: eventId } = useParams<{
    location_id: string;
    event_id: string;
  }>();

  const [imageSliderIndex, setImageSliderIndex] = useState<number | null>(null);
  const [isOpenReportDrawer, setIsOpenReportDrawer] = useState<boolean>(false);
  const { posterDataUrls, description } = ((): {
    posterDataUrls: string[];
    description: string;
  } => {
    const selector = useReportEventAddDetailsStore?.getState?.();
    if (selector) {
      return {
        posterDataUrls: selector.posterDataUrls || [],
        description: selector.description || ''
      };
    }

    return { posterDataUrls: [], description: '' };
  })();

  const handleHistoryBack = useHandleHistoryBack();

  const toast = useToast();
  const { eventDetail, isLoading, error } = useLocationEventDetail(locationId || '', eventId || '');

  const displayPosterImageUrls = useMemo(() => {
    if (posterDataUrls && posterDataUrls.length > 0) return posterDataUrls;
    return eventDetail?.posterImageUrls ?? [];
  }, [posterDataUrls, eventDetail?.posterImageUrls]);

  const displayDescription = useMemo(() => {
    if (description && description.length > 0) return description;
    return eventDetail?.description;
  }, [description, eventDetail?.description]);

  const { data: relatedEvents } = useLocationEventsQuery({
    artistId: eventDetail?.artist?.artistId,
    memberIds: eventDetail?.members?.map((member) => member.memberId),
    latitude: eventDetail?.location.latitude,
    longitude: eventDetail?.location.longitude
  });

  const BoxTabElements = [
    <div key="0" className="flex items-center gap-1 text-main-light-4 dark:text-main-dark-4">
      <InformationIcon className="w-[20px] h-[20px] fill-main-light-4 dark:fill-main-dark-4" />
      {t('OfflineEventDetail.tabToInformation')}
    </div>,
    <div key="1" className="flex items-center gap-1 text-main-light-4 dark:text-main-dark-4">
      <PresentIcon className="w-[20px] h-[20px] fill-main-light-4 dark:fill-main-dark-4" />
      {t('OfflineEventDetail.tabToPresent')}
    </div>
  ];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const boxTabIndex = urlParams.get('boxTabIndex');

    if (boxTabIndex !== null) {
      const tabIndex = Number.parseInt(boxTabIndex);
      if (tabIndex > 0) {
        const element = document.getElementById(`${boxTabIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, []);

  const isGoodsByCategoryEmpty =
    eventDetail?.goodsByCategory &&
    Object.values(eventDetail.goodsByCategory).every(
      (category) => Array.isArray(category) && category.length === 0
    );

  const showReportToastMessage = () => {
    toast({
      position: 'top',
      duration: 2000,
      render: () => (
        <BaseToastMessageBox
          position={'top'}
          startElement={
            <div className="flex items-center justify-center rounded-full w-[15px] h-[15px] bg-onSuccess-light dark:bg-onSuccess-dark">
              <CheckIcon className="fill-pure-white w-[15px] h-[15px]" />
            </div>
          }
          message={t('ReportLocationEventBottomDrawer.toastMessage')}
        />
      )
    });
  };

  const handleSelectedBoxTab = (index: number) => {
    if (index === 0) return;
    const element = document.getElementById(index.toString()) as HTMLElement | null;
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  if (isLoading || error || !eventDetail) {
    return (
      <GridSkeletons
        className={'p-1 rounded-[20px] safeAreaPaddingTop'}
        gridSize={1}
        skeletonSize={1}
        skeletonHeight={'480px'}
      />
    );
  }

  return (
    <div className="overflow-y-auto relative h-full safeAreaPaddingTop">
      <EventPoster eventDetail={eventDetail} onClosed={() => handleHistoryBack()} />
      {eventDetail?.category === 'birthday_cafe' && <EventHeader eventDetail={eventDetail} />}

      {eventDetail?.category === 'billboard_ad' && (
        <hr className="border-separator-light-1 dark:border-separator-dark-1" />
      )}

      {(eventDetail?.category === 'birthday_cafe' || eventDetail?.category === 'exhibition') && (
        <div className="px-[20px] py-[4px]">
          <BoxTabs tabLabels={BoxTabElements} onTabSelected={handleSelectedBoxTab} />
        </div>
      )}

      <div id="0" className="p-[20px]">
        {displayPosterImageUrls && (
          <div className="flex flex-col gap-5">
            {displayPosterImageUrls.map((imageUrl: string, index: number) => (
              <div className="relative" key={imageUrl}>
                <img
                  src={imageUrl}
                  alt={`PosterImage ${index}`}
                  className={'w-full rounded-[20px] object-cover'}
                />
                {
                  <div className={'absolute inset-0 flex items-end justify-end'}>
                    <div className="mb-[12px] mr-[12px]">
                      <ViewLargerButton onClick={() => setImageSliderIndex(index + 1)} />
                    </div>
                  </div>
                }
              </div>
            ))}
            {eventDetail?.category === 'billboard_ad' && (
              <ReportLocationEventBox onClick={() => setIsOpenReportDrawer(true)} />
            )}
          </div>
        )}
      </div>
      {imageSliderIndex !== null && (
        <FullScreenImageSlider
          isOpen={imageSliderIndex !== null}
          imageUrls={[
            ...displayPosterImageUrls.slice(imageSliderIndex),
            ...displayPosterImageUrls.slice(0, imageSliderIndex)
          ]}
          onClosed={() => setImageSliderIndex(null)}
        />
      )}

      {displayDescription && (
        <p className="mx-[20px] text-[15px] text-text-main-lv2">{displayDescription}</p>
      )}
      {eventDetail.tweet !== undefined && (
        <div className="p-[20px]">
          <button
            type="button"
            className="flex items-center rounded-xl border w-auto cursor-pointer"
            onClick={() => {
              if (eventDetail.tweet) window.location.href = eventDetail.tweet.tweetUrl;
            }}
          >
            <div className="flex flex-col px-[18px] py-3 min-w-[230px]">
              <div className="font-bold text-main-light-3 dark:text-main-dark-3 overflow-hidden text-ellipsis whitespace-nowrap">
                {ellipsis(eventDetail.tweet.authorName?.replace('@', ''), 7)}{' '}
                {t('OfflineEventDetail.moveToX')}
              </div>
              <div className="text-sm max-w-[190px] text-sub-light-1 dark:text-sub-dark-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {eventDetail.tweet.content}
              </div>
              <div className="flex items-center text-sm mt-2 text-primary-light dark:text-textPrimary-dark">
                {t('OfflineEventDetail.moveToPost')}
                <img src={move_icon} alt="move_icon" className="ml-1" />
              </div>
            </div>
            <div className="w-[103px] aspect-square overflow-hidden rounded-tr-xl rounded-br-xl flex-shrink-0">
              <img
                src={eventDetail.tweet.imageUrls[0]?.replaceAll("'", '').trim()}
                alt="tweet_image"
                className="w-full h-full object-cover"
              />
            </div>
          </button>
        </div>
      )}

      <hr className="mx-[20px] my-[28px] border-separator-light-1 dark:border-separator-dark-1" />

      {(eventDetail?.category === 'birthday_cafe' || eventDetail?.category === 'exhibition') && (
        <div>
          <div>
            <EventGoodsGiftTypes eventDetail={eventDetail} />
          </div>

          <hr className="mx-[20px] my-[28px] border-separator-light-1 dark:border-separator-dark-1" />
        </div>
      )}

      {(eventDetail?.category === 'birthday_cafe' || eventDetail?.category === 'exhibition') &&
        !isGoodsByCategoryEmpty && (
          <div>
            <div id="1">
              {/* Badge 추가 */}
              <EventGoodsByCategory eventDetail={eventDetail} />
            </div>

            <hr className="mx-[20px] my-[28px] border-separator-light-1 dark:border-separator-dark-1" />
          </div>
        )}

      {/* Location Page 와 동일하게 수정 */}
      <div>
        <RelatedEvents relatedEvents={relatedEvents?.events} eventDetail={eventDetail} />
      </div>

      <hr className="mx-[20px] my-[28px] border-separator-light-1 dark:border-separator-dark-1" />

      <div>
        {eventDetail.category === 'billboard_ad' ? (
          <div className="px-[20px]">
            <PostItEventPosts
              locationId={eventDetail.location.locationId}
              eventId={eventDetail.eventId}
            />
          </div>
        ) : (
          <EventPosts locationId={eventDetail.location.locationId} eventId={eventDetail.eventId} />
        )}
      </div>

      <div className="h-24" />

      {/* LocationBottomBar 와 동일 하게 맞춤 */}
      <LocationBottomBar
        locationId={eventDetail.location.locationId}
        locationName={eventDetail.location.name}
        locationAddress={eventDetail.location.streetAddress}
        coordinates={eventDetail.location.getCoordinates()}
        kakaoMapUID={eventDetail.location.kakaoUid}
        isLiked={eventDetail.location.isLiked}
      />
      {isOpenReportDrawer && (
        <ReportLocationEventBottomDrawer
          eventId={eventDetail.eventId}
          isOpen={isOpenReportDrawer}
          onReport={() => showReportToastMessage()}
          onClose={() => setIsOpenReportDrawer(false)}
        />
      )}
    </div>
  );
};

export default OfflineEventDetailPage;
